import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  Settings2,
} from 'lucide-react';
import { GenericField, FieldType } from '../../types';

interface GenericSectionProps {
  id: string;
  title: string;
  fields: GenericField[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onTitleChange: (newTitle: string) => void;
  onFieldUpdate: (fieldId: string, value: string) => void;
  onFieldAdd: (field: GenericField) => void;
  onFieldDelete: (fieldId: string) => void;
  onFieldConfigChange: (fieldId: string, updates: Partial<GenericField>) => void;
  onDeleteSection?: () => void;
}

const FIELD_TYPE_OPTIONS: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'longText', label: 'Long Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
  { value: 'select', label: 'Select' },
];

export default function GenericSection({
  id,
  title,
  fields,
  isExpanded,
  onToggleExpand,
  onTitleChange,
  onFieldUpdate,
  onFieldAdd,
  onFieldDelete,
  onFieldConfigChange,
  onDeleteSection,
}: GenericSectionProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<FieldType>('text');
  const [configuringFieldId, setConfiguringFieldId] = useState<string | null>(null);

  const handleTitleSave = () => {
    if (editedTitle.trim()) {
      onTitleChange(editedTitle.trim());
    } else {
      setEditedTitle(title);
    }
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setEditedTitle(title);
    setIsEditingTitle(false);
  };

  const handleAddField = () => {
    if (!newFieldLabel.trim()) return;

    const newField: GenericField = {
      id: `${id}_${Date.now()}`,
      label: newFieldLabel.trim(),
      value: '',
      type: newFieldType,
      placeholder: `Enter ${newFieldLabel.trim().toLowerCase()}...`,
    };

    onFieldAdd(newField);
    setNewFieldLabel('');
    setNewFieldType('text');
    setShowAddField(false);
  };

  const renderFieldInput = (field: GenericField) => {
    const baseClass = 'w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent';

    switch (field.type) {
      case 'longText':
        return (
          <textarea
            value={field.value}
            onChange={(e) => onFieldUpdate(field.id, e.target.value)}
            className={baseClass + ' resize-none'}
            rows={3}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={field.value}
            onChange={(e) => onFieldUpdate(field.id, e.target.value)}
            className={baseClass}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={field.value}
            onChange={(e) => onFieldUpdate(field.id, e.target.value)}
            className={baseClass}
            placeholder={field.placeholder || '0'}
          />
        );
      case 'email':
        return (
          <input
            type="email"
            value={field.value}
            onChange={(e) => onFieldUpdate(field.id, e.target.value)}
            className={baseClass}
            placeholder={field.placeholder || 'email@example.com'}
          />
        );
      case 'phone':
        return (
          <input
            type="tel"
            value={field.value}
            onChange={(e) => onFieldUpdate(field.id, e.target.value)}
            className={baseClass}
            placeholder={field.placeholder || '(555) 555-5555'}
          />
        );
      case 'url':
        return (
          <input
            type="url"
            value={field.value}
            onChange={(e) => onFieldUpdate(field.id, e.target.value)}
            className={baseClass}
            placeholder={field.placeholder || 'https://'}
          />
        );
      case 'select':
        return (
          <select
            value={field.value}
            onChange={(e) => onFieldUpdate(field.id, e.target.value)}
            className={baseClass}
          >
            <option value="">Select...</option>
            {(field.options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            value={field.value}
            onChange={(e) => onFieldUpdate(field.id, e.target.value)}
            className={baseClass}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
          />
        );
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors">
        {isEditingTitle ? (
          <div className="flex items-center gap-1 flex-1 mr-2">
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSave();
                if (e.key === 'Escape') handleTitleCancel();
              }}
              className="flex-1 px-2 py-0.5 text-sm border border-accent/50 rounded focus:outline-none focus:ring-1 focus:ring-accent"
              autoFocus
            />
            <button
              onClick={handleTitleSave}
              className="p-0.5 text-green-600 hover:text-green-700"
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleTitleCancel}
              className="p-0.5 text-red-600 hover:text-red-700"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={onToggleExpand}
            className="flex items-center gap-2 flex-1 text-left"
          >
            <span className="font-medium text-sm text-gray-800">{title}</span>
          </button>
        )}

        <div className="flex items-center gap-1">
          {!isEditingTitle && (
            <button
              onClick={() => {
                setEditedTitle(title);
                setIsEditingTitle(true);
              }}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              title="Edit title"
            >
              <Edit3 size={12} />
            </button>
          )}
          {onDeleteSection && !isEditingTitle && (
            <button
              onClick={onDeleteSection}
              className="p-1 text-gray-400 hover:text-red-600 rounded"
              title="Delete section"
            >
              <Trash2 size={12} />
            </button>
          )}
          <button
            onClick={onToggleExpand}
            className="p-1 text-gray-500 hover:text-gray-700"
          >
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-3 space-y-3">
          {fields.map((field) => (
            <div key={field.id} className="group relative">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-600">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() =>
                      setConfiguringFieldId(
                        configuringFieldId === field.id ? null : field.id
                      )
                    }
                    className="p-0.5 text-gray-400 hover:text-accent"
                    title="Configure field"
                  >
                    <Settings2 size={11} />
                  </button>
                  <button
                    onClick={() => onFieldDelete(field.id)}
                    className="p-0.5 text-gray-400 hover:text-red-600"
                    title="Delete field"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>

              {/* Field Configuration Panel */}
              {configuringFieldId === field.id && (
                <div className="mb-2 p-2 bg-accent/10 border border-accent/30 rounded-md space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600 whitespace-nowrap">Type:</label>
                    <select
                      value={field.type}
                      onChange={(e) =>
                        onFieldConfigChange(field.id, { type: e.target.value as FieldType })
                      }
                      className="flex-1 text-xs px-1.5 py-1 border border-gray-200 rounded"
                    >
                      {FIELD_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600 whitespace-nowrap">Label:</label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) =>
                        onFieldConfigChange(field.id, { label: e.target.value })
                      }
                      className="flex-1 text-xs px-1.5 py-1 border border-gray-200 rounded"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600 whitespace-nowrap">Placeholder:</label>
                    <input
                      type="text"
                      value={field.placeholder || ''}
                      onChange={(e) =>
                        onFieldConfigChange(field.id, { placeholder: e.target.value })
                      }
                      className="flex-1 text-xs px-1.5 py-1 border border-gray-200 rounded"
                    />
                  </div>
                  {field.type === 'select' && (
                    <div className="flex items-start gap-2">
                      <label className="text-xs text-gray-600 whitespace-nowrap pt-1">Options:</label>
                      <input
                        type="text"
                        value={(field.options || []).join(', ')}
                        onChange={(e) =>
                          onFieldConfigChange(field.id, {
                            options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                          })
                        }
                        className="flex-1 text-xs px-1.5 py-1 border border-gray-200 rounded"
                        placeholder="Option 1, Option 2, Option 3"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`required-${field.id}`}
                      checked={field.required || false}
                      onChange={(e) =>
                        onFieldConfigChange(field.id, { required: e.target.checked })
                      }
                      className="rounded text-accent"
                    />
                    <label htmlFor={`required-${field.id}`} className="text-xs text-gray-600">
                      Required
                    </label>
                  </div>
                  <button
                    onClick={() => setConfiguringFieldId(null)}
                    className="text-xs text-accent hover:text-accent-dark"
                  >
                    Done
                  </button>
                </div>
              )}

              {renderFieldInput(field)}
            </div>
          ))}

          {/* Add Field */}
          {showAddField ? (
            <div className="p-2 border border-dashed border-gray-300 rounded-md space-y-2">
              <input
                type="text"
                value={newFieldLabel}
                onChange={(e) => setNewFieldLabel(e.target.value)}
                placeholder="Field label"
                className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-accent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddField();
                  if (e.key === 'Escape') setShowAddField(false);
                }}
              />
              <div className="flex items-center gap-2">
                <select
                  value={newFieldType}
                  onChange={(e) => setNewFieldType(e.target.value as FieldType)}
                  className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded"
                >
                  {FIELD_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddField}
                  className="px-2 py-1 text-xs bg-accent text-white rounded hover:bg-accent-dark"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddField(false);
                    setNewFieldLabel('');
                    setNewFieldType('text');
                  }}
                  className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddField(true)}
              className="w-full flex items-center justify-center gap-1 py-1.5 text-xs text-gray-500 hover:text-accent border border-dashed border-gray-300 hover:border-accent rounded-md transition-colors"
            >
              <Plus size={12} />
              Add Field
            </button>
          )}
        </div>
      )}
    </div>
  );
}
